/**
 * TimelineRenderer — 精美时间轴渲染器
 *
 * 将 ```timeline 代码块渲染为高端垂直时间轴。
 * 设计风格与 EChartsRenderer / TopologyRenderer / WorkflowRenderer 完全一致：
 * 浅色背景 (#FAFAFA) + Institutional Minimalist 品牌色系。
 * 零依赖，纯 CSS + React。
 */

import React, { useState, useMemo, useId } from 'react';

// ─── 事件类型样式（与品牌色系一致）───────────
const TYPE_STYLES: Record<string, {
    dot: string;
    border: string;
    bg: string;
    badge: string;
}> = {
    milestone: { dot: '#3B82F6', border: '#DBEAFE', bg: '#EFF6FF', badge: '#3B82F6' },
    warning:   { dot: '#F59E0B', border: '#FDE68A', bg: '#FFFBEB', badge: '#D97706' },
    critical:  { dot: '#EF4444', border: '#FECACA', bg: '#FEF2F2', badge: '#DC2626' },
    default:   { dot: '#6366F1', border: '#E0E7FF', bg: '#EEF2FF', badge: '#6366F1' },
};

interface TimelineEvent {
    date: string;
    title: string;
    description?: string;
    type?: string;
}

interface TimelineRendererProps {
    optionString: string;
}

const TimelineRenderer: React.FC<TimelineRendererProps> = ({ optionString }) => {
    const [error, setError] = useState<string>('');
    const uniqueId = useId().replace(/:/g, '_');

    const config = useMemo(() => {
        try {
            const parsed = JSON.parse(optionString.trim());
            return {
                title: parsed.title || 'Timeline',
                events: (parsed.events || []) as TimelineEvent[],
            };
        } catch (err: any) {
            setError(err.message);
            return null;
        }
    }, [optionString]);

    if (error || !config) {
        return (
            <div style={{
                margin: '12px 0', padding: '16px', background: '#F9FAFB',
                border: '1px solid #E5E7EB', borderRadius: '8px', position: 'relative',
            }}>
                <div style={{ position: 'absolute', top: '8px', right: '12px', fontSize: '11px', color: '#9CA3AF', fontWeight: 600 }}>
                    TIMELINE
                </div>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '13px', color: '#374151', fontFamily: 'monospace' }}>
                    {optionString}
                </pre>
            </div>
        );
    }

    return (
        <div id={`timeline-${uniqueId}`} style={{
            margin: '16px 0', border: '1px solid #E5E7EB', borderRadius: '12px',
            overflow: 'hidden', background: '#FAFAFA', position: 'relative',
        }}>
            {/* 入场动画 */}
            <style>{`
                @keyframes tl-slide-${uniqueId} {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .tl-card-${uniqueId} {
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .tl-card-${uniqueId}:hover {
                    transform: translateX(3px);
                    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
                }
            `}</style>

            {/* 标题栏 — 与其他渲染器完全一致 */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: '#F3F4F6', borderBottom: '1px solid #E5E7EB',
            }}>
                <div style={{ fontSize: '13px', color: '#1F2937', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                    {config.title}
                </div>
                <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#6366F1', display: 'inline-block' }} />
                    TIMELINE · {config.events.length} events
                </div>
            </div>

            {/* 时间轴主体 */}
            <div style={{ padding: '20px 16px 16px 16px', position: 'relative' }}>
                {config.events.map((event, index) => {
                    const style = TYPE_STYLES[event.type || 'default'] || TYPE_STYLES.default;
                    const isLast = index === config.events.length - 1;

                    return (
                        <div key={index} style={{
                            display: 'flex', alignItems: 'flex-start', position: 'relative',
                            paddingLeft: '36px',
                            marginBottom: isLast ? 0 : '4px',
                            minHeight: isLast ? 'auto' : '64px',
                            animation: `tl-slide-${uniqueId} 0.35s ease ${index * 0.08}s both`,
                        }}>
                            {/* 渐变竖线 */}
                            {!isLast && (
                                <div style={{
                                    position: 'absolute', left: '13px', top: '18px',
                                    width: '2px', bottom: '-4px',
                                    background: 'linear-gradient(to bottom, #CBD5E1, #E2E8F0)',
                                    borderRadius: '1px',
                                }} />
                            )}

                            {/* 圆点 + 光环 */}
                            <div style={{
                                position: 'absolute', left: '6px', top: '10px',
                                width: '16px', height: '16px', borderRadius: '50%',
                                background: style.dot,
                                border: '3px solid #FAFAFA',
                                boxShadow: `0 0 0 2px ${style.border}, 0 1px 4px rgba(0,0,0,0.08)`,
                                zIndex: 2,
                            }} />

                            {/* 事件卡片 */}
                            <div
                                className={`tl-card-${uniqueId}`}
                                style={{
                                    flex: 1,
                                    background: style.bg,
                                    border: `1px solid ${style.border}`,
                                    borderRadius: '8px',
                                    padding: '10px 14px',
                                    marginBottom: '8px',
                                    cursor: 'default',
                                }}>
                                {/* 日期胶囊 + 标题 */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: event.description ? '5px' : 0 }}>
                                    <span style={{
                                        display: 'inline-block',
                                        fontSize: '11px', fontWeight: 600,
                                        fontFamily: 'Inter, monospace',
                                        color: '#FFFFFF',
                                        background: style.badge,
                                        padding: '2px 8px',
                                        borderRadius: '100px',
                                        letterSpacing: '0.01em',
                                    }}>
                                        {event.date}
                                    </span>
                                    <span style={{
                                        fontSize: '13px', fontWeight: 600, color: '#1F2937',
                                        fontFamily: 'Inter, "Noto Sans SC", sans-serif',
                                    }}>
                                        {event.title}
                                    </span>
                                </div>
                                {/* 描述 */}
                                {event.description && (
                                    <div style={{
                                        fontSize: '12.5px', color: '#6B7280', lineHeight: 1.55,
                                        fontFamily: 'Inter, "Noto Sans SC", sans-serif',
                                    }}>
                                        {event.description}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default TimelineRenderer;
